/**
 * Which country a timezone is in.
 *
 * Used for one thing: opening a phone field on the right dialling code before anybody has typed
 * anything.
 *
 * **Not the browser's language.** `navigator.language` is a preference, not a location — an
 * agency in Lagos running Windows in English reports `en-US`, and a phone field that opens on
 * `+1` because of it is wrong for every number that agency will ever enter. The timezone is set
 * from where the machine actually is, so it is the honest signal of the two.
 *
 * **Common zones, not every zone.** IANA carries several hundred names, many of them historical
 * aliases kept only so old data still parses. What is here is the zone or zones a current
 * machine is realistically set to, and anything unlisted falls back to `DEFAULT_COUNTRY` — which
 * is the same answer the field would have given with no detection at all. Getting this wrong
 * costs one dropdown; there is no need to be exhaustive about it.
 */

const ZONES =
  "Africa/Abidjan CI,Africa/Accra GH,Africa/Addis_Ababa ET,Africa/Algiers DZ,Africa/Asmara ER," +
  "Africa/Bamako ML,Africa/Bangui CF,Africa/Banjul GM,Africa/Bissau GW,Africa/Blantyre MW," +
  "Africa/Brazzaville CG,Africa/Bujumbura BI,Africa/Cairo EG,Africa/Casablanca MA,Africa/Conakry GN," +
  "Africa/Dakar SN,Africa/Dar_es_Salaam TZ,Africa/Djibouti DJ,Africa/Douala CM,Africa/Freetown SL," +
  "Africa/Gaborone BW,Africa/Harare ZW,Africa/Johannesburg ZA,Africa/Juba SS,Africa/Kampala UG," +
  "Africa/Khartoum SD,Africa/Kigali RW,Africa/Kinshasa CD,Africa/Lagos NG,Africa/Libreville GA," +
  "Africa/Lome TG,Africa/Luanda AO,Africa/Lubumbashi CD,Africa/Lusaka ZM,Africa/Malabo GQ," +
  "Africa/Maputo MZ,Africa/Maseru LS,Africa/Mbabane SZ,Africa/Mogadishu SO,Africa/Monrovia LR," +
  "Africa/Nairobi KE,Africa/Ndjamena TD,Africa/Niamey NE,Africa/Nouakchott MR,Africa/Ouagadougou BF," +
  "Africa/Porto-Novo BJ,Africa/Sao_Tome ST,Africa/Tripoli LY,Africa/Tunis TN,Africa/Windhoek NA," +
  "America/Anchorage US,America/Antigua AG,America/Argentina/Buenos_Aires AR,America/Aruba AW," +
  "America/Asuncion PY,America/Barbados BB,America/Belize BZ,America/Bogota CO,America/Caracas VE," +
  "America/Cayenne GF,America/Cayman KY,America/Chicago US,America/Costa_Rica CR,America/Curacao CW," +
  "America/Denver US,America/Detroit US,America/Edmonton CA,America/El_Salvador SV," +
  "America/Grand_Turk TC,America/Guatemala GT,America/Guayaquil EC,America/Guyana GY," +
  "America/Halifax CA,America/Havana CU,America/Jamaica JM,America/La_Paz BO,America/Lima PE," +
  "America/Los_Angeles US,America/Managua NI,America/Martinique MQ,America/Mexico_City MX," +
  "America/Montevideo UY,America/Nassau BS,America/New_York US,America/Panama PA," +
  "America/Paramaribo SR,America/Phoenix US,America/Port-au-Prince HT,America/Port_of_Spain TT," +
  "America/Puerto_Rico PR,America/Regina CA,America/Santiago CL,America/Santo_Domingo DO," +
  "America/Sao_Paulo BR,America/St_Kitts KN,America/St_Lucia LC,America/St_Vincent VC," +
  "America/Tegucigalpa HN,America/Toronto CA,America/Vancouver CA,America/Winnipeg CA," +
  "Asia/Almaty KZ,Asia/Amman JO,Asia/Baghdad IQ,Asia/Bahrain BH,Asia/Baku AZ,Asia/Bangkok TH," +
  "Asia/Beirut LB,Asia/Bishkek KG,Asia/Brunei BN,Asia/Colombo LK,Asia/Damascus SY,Asia/Dhaka BD," +
  "Asia/Dubai AE,Asia/Dushanbe TJ,Asia/Hebron PS,Asia/Ho_Chi_Minh VN,Asia/Hong_Kong HK," +
  "Asia/Jakarta ID,Asia/Jayapura ID,Asia/Jerusalem IL,Asia/Kabul AF,Asia/Karachi PK," +
  "Asia/Kathmandu NP,Asia/Kolkata IN,Asia/Kuala_Lumpur MY,Asia/Kuwait KW,Asia/Macau MO," +
  "Asia/Makassar ID,Asia/Manila PH,Asia/Muscat OM,Asia/Nicosia CY,Asia/Phnom_Penh KH," +
  "Asia/Pyongyang KP,Asia/Qatar QA,Asia/Riyadh SA,Asia/Seoul KR,Asia/Shanghai CN," +
  "Asia/Singapore SG,Asia/Taipei TW,Asia/Tashkent UZ,Asia/Tbilisi GE,Asia/Tehran IR,Asia/Thimphu BT," +
  "Asia/Tokyo JP,Asia/Ulaanbaatar MN,Asia/Vientiane LA,Asia/Yangon MM,Asia/Yerevan AM," +
  "Atlantic/Bermuda BM,Atlantic/Cape_Verde CV,Atlantic/Faroe FO,Atlantic/Reykjavik IS," +
  "Australia/Adelaide AU,Australia/Brisbane AU,Australia/Darwin AU,Australia/Melbourne AU," +
  "Australia/Perth AU,Australia/Sydney AU," +
  "Europe/Amsterdam NL,Europe/Andorra AD,Europe/Athens GR,Europe/Belgrade RS,Europe/Berlin DE," +
  "Europe/Bratislava SK,Europe/Brussels BE,Europe/Bucharest RO,Europe/Budapest HU," +
  "Europe/Chisinau MD,Europe/Copenhagen DK,Europe/Dublin IE,Europe/Gibraltar GI," +
  "Europe/Guernsey GG,Europe/Helsinki FI,Europe/Isle_of_Man IM,Europe/Istanbul TR," +
  "Europe/Jersey JE,Europe/Kyiv UA,Europe/Kiev UA,Europe/Lisbon PT,Europe/Ljubljana SI," +
  "Europe/London GB,Europe/Luxembourg LU,Europe/Madrid ES,Europe/Malta MT,Europe/Minsk BY," +
  "Europe/Monaco MC,Europe/Moscow RU,Europe/Oslo NO,Europe/Paris FR,Europe/Podgorica ME," +
  "Europe/Prague CZ,Europe/Riga LV,Europe/Rome IT,Europe/San_Marino SM,Europe/Sarajevo BA," +
  "Europe/Skopje MK,Europe/Sofia BG,Europe/Stockholm SE,Europe/Tallinn EE,Europe/Tirane AL," +
  "Europe/Vaduz LI,Europe/Vatican VA,Europe/Vienna AT,Europe/Vilnius LT,Europe/Warsaw PL," +
  "Europe/Zagreb HR,Europe/Zurich CH," +
  "Indian/Antananarivo MG,Indian/Comoro KM,Indian/Maldives MV,Indian/Mauritius MU,Indian/Mayotte YT," +
  "Indian/Reunion RE," +
  "Pacific/Auckland NZ,Pacific/Fiji FJ,Pacific/Guam GU,Pacific/Honolulu US,Pacific/Majuro MH," +
  "Pacific/Noumea NC,Pacific/Pago_Pago AS,Pacific/Palau PW,Pacific/Port_Moresby PG," +
  "Pacific/Rarotonga CK,Pacific/Tarawa KI,Pacific/Tongatapu TO,Pacific/Funafuti TV,Pacific/Apia WS";

const COUNTRY_BY_ZONE = new Map<string, string>(
  ZONES.split(",").map((entry) => {
    const [zone, code] = entry.trim().split(" ");
    return [zone, code] as [string, string];
  }),
);

/** The ISO country code for an IANA timezone, or null when it is not one we list. */
export function countryForTimezone(zone: string | null | undefined): string | null {
  if (!zone) return null;
  return COUNTRY_BY_ZONE.get(zone.trim()) ?? null;
}

/**
 * The country this machine is in, as far as anything can tell.
 *
 * Returns null rather than guessing when the zone is unlisted or the API is unavailable, so the
 * caller decides what the default is — this module has no business knowing that.
 */
export function detectedCountry(): string | null {
  try {
    return countryForTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone);
  } catch {
    return null;
  }
}
